import type {Order, Price} from '@wix/ambassador-ep-orders/types'

const toAmount = (price?: Price | null) => {
  const value = Number(price?.amount)

  return Number.isFinite(value) ? value : 0
}

export const isPositivePrice = (price?: Price | null) => toAmount(price) > 0

export const getOrderPaid = (order?: Order | null) => {
  const paid = order?.balanceSummary?.paid

  return isPositivePrice(paid) ? paid : undefined
}

export const getOrderCancellationFee = (order?: Order | null): Price | undefined => {
  const additionalTotal = order?.priceSummary?.totalAdditionalFees

  if (isPositivePrice(additionalTotal)) {
    return additionalTotal
  }

  const feeLine = order?.additionalFees?.find((fee) => isPositivePrice(fee.price))

  if (feeLine?.price) {
    return feeLine.price
  }

  const paid = toAmount(order?.balanceSummary?.paid)
  const total = toAmount(order?.priceSummary?.total)
  const subtotal = toAmount(order?.priceSummary?.subtotal)

  if (paid > total && total > 0 && subtotal === 0) {
    return order?.priceSummary?.total
  }

  return undefined
}

export const hasOrderCancellationFee = (order?: Order | null) =>
  Boolean(getOrderCancellationFee(order))

export const isUnchangedPrepaidOrder = (order?: Order | null) => {
  if (hasOrderCancellationFee(order)) {
    return false
  }

  const subtotal = toAmount(order?.priceSummary?.subtotal)
  const lineItem = toAmount(order?.lineItems?.[0]?.lineItemPrice)
  const total = toAmount(order?.priceSummary?.total)
  const paid = toAmount(order?.balanceSummary?.paid)

  if (total <= 0 || (subtotal <= 0 && lineItem <= 0)) {
    return false
  }

  return paid === 0 || Math.abs(paid - total) < 0.01
}

export const getOriginalPrepaidTotal = (order?: Order | null): Price | undefined => {
  const paid = getOrderPaid(order)

  if (hasOrderCancellationFee(order) && paid) {
    return paid
  }

  return undefined
}

export const getOrderRefund = (order?: Order | null): Price | undefined => {
  if (isPositivePrice(order?.balanceSummary?.refunded)) {
    return order?.balanceSummary?.refunded
  }

  return undefined
}
